/**
 * Social Media URL Formatter Utility
 * Automatically formats usernames/handles into proper public URLs
 */

/**
 * Format social media username to proper URL
 * @param {string} platform - Social media platform name
 * @param {string} input - User input (username, handle, or full URL)
 * @returns {string} - Properly formatted URL or original input if already valid
 */
export const formatSocialMediaUrl = (platform, input) => {
  if (!input || !input?.trim()) return '';
  
  const trimmedInput = input?.trim();
  
  // If already a valid URL, return as is
  if (trimmedInput?.startsWith('http://') || trimmedInput?.startsWith('https://')) {
    return trimmedInput;
  }
  
  // Remove @ symbol if present
  const cleanInput = trimmedInput?.replace(/^@/, '');
  
  // Platform-specific URL formatting
  switch (platform?.toLowerCase()) {
    case 'instagram':
      return `https://www.instagram.com/${cleanInput}`;
    
    case 'twitter':
      return `https://twitter.com/${cleanInput}`;
    
    case 'facebook':
      return `https://www.facebook.com/${cleanInput}`;
    
    case 'soundcloud':
      return `https://soundcloud.com/${cleanInput}`;
    
    case 'snapchat':
      return `https://www.snapchat.com/add/${cleanInput}`;
    
    case 'linkedin':
      return `https://www.linkedin.com/in/${cleanInput}`;
    
    case 'imdb':
      // Handle both name and numeric ID formats
      if (cleanInput?.startsWith('nm') || /^\d+$/?.test(cleanInput)) {
        return `https://www.imdb.com/name/${cleanInput?.startsWith('nm') ? cleanInput : 'nm' + cleanInput}/`;
      }
      return `https://www.imdb.com/find?q=${encodeURIComponent(cleanInput)}`;
    
    default:
      return trimmedInput;
  }
};

/**
 * Extract username from social media URL
 * @param {string} platform - Social media platform name
 * @param {string} url - Full URL
 * @returns {string} - Extracted username or original URL if not recognizable
 */
export const extractUsernameFromUrl = (platform, url) => {
  if (!url || !url?.trim()) return '';
  
  const trimmedUrl = url?.trim();
  
  // If not a URL, return as is
  if (!trimmedUrl?.startsWith('http://') && !trimmedUrl?.startsWith('https://')) {
    return trimmedUrl;
  }
  
  try {
    const urlObj = new URL(trimmedUrl);
    const pathname = urlObj?.pathname;
    
    switch (platform?.toLowerCase()) {
      case 'instagram':
        if (urlObj?.hostname?.includes('instagram.com')) {
          const match = pathname?.match(/^\/([^\/\?]+)/);
          return match ? match?.[1] : trimmedUrl;
        }
        break;
      
      case 'twitter':
        if (urlObj?.hostname?.includes('twitter.com') || urlObj?.hostname?.includes('x.com')) {
          const match = pathname?.match(/^\/([^\/\?]+)/);
          return match ? match?.[1] : trimmedUrl;
        }
        break;
      
      case 'facebook':
        if (urlObj?.hostname?.includes('facebook.com')) {
          const match = pathname?.match(/^\/([^\/\?]+)/);
          return match ? match?.[1] : trimmedUrl;
        }
        break;
      
      case 'soundcloud':
        if (urlObj?.hostname?.includes('soundcloud.com')) {
          const match = pathname?.match(/^\/([^\/\?]+)/);
          return match ? match?.[1] : trimmedUrl;
        }
        break;
      
      case 'snapchat':
        if (urlObj?.hostname?.includes('snapchat.com')) {
          const match = pathname?.match(/^\/add\/([^\/\?]+)/);
          return match ? match?.[1] : trimmedUrl;
        }
        break;
      
      case 'linkedin':
        if (urlObj?.hostname?.includes('linkedin.com')) {
          const match = pathname?.match(/^\/in\/([^\/\?]+)/);
          return match ? match?.[1] : trimmedUrl;
        }
        break;
      
      case 'imdb':
        if (urlObj?.hostname?.includes('imdb.com')) {
          const match = pathname?.match(/^\/name\/([^\/\?]+)/);
          return match ? match?.[1] : trimmedUrl;
        }
        break;
      
      default:
        return trimmedUrl;
    }
  } catch (error) {
    return trimmedUrl;
  }
  
  return trimmedUrl;
};

/**
 * Validate if input is a proper social media handle/username
 * @param {string} platform - Social media platform name
 * @param {string} input - User input
 * @returns {object} - { isValid: boolean, message: string }
 */
export const validateSocialMediaHandle = (platform, input) => {
  if (!input || !input?.trim()) {
    return { isValid: true, message: '' };
  }
  
  const trimmedInput = input?.trim();
  
  // If it's already a URL, validate URL format
  if (trimmedInput?.startsWith('http://') || trimmedInput?.startsWith('https://')) {
    try {
      new URL(trimmedInput);
      return { isValid: true, message: '' };
    } catch {
      return { isValid: false, message: 'Please enter a valid URL' };
    }
  }
  
  // Clean input for validation
  const cleanInput = trimmedInput?.replace(/^@/, '');
  
  // Platform-specific validation
  switch (platform?.toLowerCase()) {
    case 'instagram': case'twitter': case'facebook': case'soundcloud': case'snapchat':
      if (!/^[a-zA-Z0-9._-]+$/?.test(cleanInput)) {
        return { 
          isValid: false, 
          message: 'Username can only contain letters, numbers, dots, underscores, and hyphens' 
        };
      }
      if (cleanInput?.length < 1 || cleanInput?.length > 30) {
        return { 
          isValid: false, 
          message: 'Username must be between 1 and 30 characters' 
        };
      }
      break;
      
    case 'linkedin':
      if (!/^[a-zA-Z0-9-]+$/?.test(cleanInput)) {
        return { 
          isValid: false, 
          message: 'LinkedIn username can only contain letters, numbers, and hyphens' 
        };
      }
      break;
      
    case 'imdb':
      // More flexible validation for IMDB
      if (cleanInput?.length < 1) {
        return { 
          isValid: false, 
          message: 'Please enter an IMDB ID or name' 
        };
      }
      break;
      
    default:
      return { isValid: true, message: '' };
  }
  
  return { isValid: true, message: '' };
};

/**
 * Get platform display name
 * @param {string} platform - Platform key
 * @returns {string} - Display name
 */
export const getPlatformDisplayName = (platform) => {
  const displayNames = {
    instagram: 'Instagram',
    twitter: 'Twitter',
    facebook: 'Facebook',
    soundcloud: 'SoundCloud',
    snapchat: 'Snapchat',
    linkedin: 'LinkedIn',
    imdb: 'IMDB'
  };
  
  return displayNames?.[platform?.toLowerCase()] || platform;
};

/**
 * Get platform icon name for UI
 * @param {string} platform - Platform key
 * @returns {string} - Icon name
 */
export const getPlatformIcon = (platform) => {
  const icons = {
    instagram: 'Instagram',
    twitter: 'Twitter',
    facebook: 'Facebook',
    soundcloud: 'Music',
    snapchat: 'Camera',
    linkedin: 'Linkedin',
    imdb: 'Film'
  };
  
  return icons?.[platform?.toLowerCase()] || 'Link';
};

export default {
  formatSocialMediaUrl,
  extractUsernameFromUrl,
  validateSocialMediaHandle,
  getPlatformDisplayName,
  getPlatformIcon
};